import {
  V1_ADDRESS_WITH_ZIP_CODE,
  V1_BASIC_INFORMATION_PATH,
  V1_BASIC_INFO_STEP1_PATH,
  V1_BASIC_INFO_STEP2_PATH,
  V1_BASIC_INFO_STEP3_PATH,
  V1_BASIC_INFO_STEP4_COMPLETED_PATH,
  V1_BASIC_INFO_STEP4_PATH,
  V1_BLOCKED_COMPANIES_PATH,
  V1_CANDIDATES_SAVE_ACCOUNT,
  V1_CANDIDATES_VERIFY_ACCOUNT,
  V1_CANDIDATES_VERIFY_TOKEN,
  V1_CANDIDATE_RESEND_MAIL,
  V1_CANDIDATE_RESET_PASSWORD,
  V1_CANDIDATE_INTERVIEW_SLOTS_PATH,
  V1_CITY_PATH,
  V1_COMPLETED_SIGN_UP_PATH,
  V1_COUNTRY_PATH,
  V1_EMPLOYMENT_TYPE_PATH,
  V1_EXPERIENCE_PATH,
  V1_GRADUATION_CLASSIFICATION_PATH,
  V1_INCOME_PATH,
  V1_INDUSTRIES_PATH,
  V1_JOB_FEATURES_PATH,
  V1_JOB_SEARCH_CONDITION,
  V1_JOB_SEARCH_PATH,
  V1_LANGUAGE_LEVEL_PATH,
  V1_LANGUAGE_PATH,
  V1_OCCUPATION_PATH,
  V1_OFFER_REPORT_PATH,
  V1_PREFECTURE_PATH,
  V1_PROFILE_PATH,
  V1_WELCOME_GUIDE_PATH,
  V1_CANDIDATES_JOB_APPLY_PATH,
  V1_REGISTRATION_PATH,
  V1_RESUME_OPTIONAL_INFORMATION_PATH,
  V1_SAVE_CONDITION_JOB_SEARCH,
  V1_SCHOOL_CATEGORY_PATH,
  V1_SEARCH_JOB_PATH,
  V1_SESSION_PATH,
  V1_SIGN_UP_PATH,
  V1_SKILL_PATH,
  V1_SERVICES_AGENT_PATH,
} from '@/constant/routers/path';
import request from '@/services/http.service';
import type {
  AddressWithZipCodeResponse,
  ApiResponse,
  BasicInformationResponse,
  CompanyListResponse,
  CreateAccountEmailRequest,
  CreateAccountEmailResponse,
  CreateBasicInformationResponse,
  CreateBasicInformationRequest,
  CreateJobSearchConditionRequest,
  LoginRequest,
  LoginResponse,
  ProfileUpdateFormData,
  RegistrationDataRequest,
  SaveAccessTokenRequest,
  SaveCandidateAccountResponse,
  SaveResumeOptionalInformationFormData,
  VerifyAccountResponse,
  InterviewSlotCreatePayload,
} from '@/types/api/candidate';
import { ConfirmPassword } from '@/types/api/confirmPassword';
import type {
  BasicInfoStep1Request,
  BasicInfoStep1Response,
  BasicInfoStep2Request,
  BasicInfoStep2Response,
  BasicInfoStep3Request,
  BasicInfoStep3Response,
  BasicInfoStep4CompletedResponse,
  BasicInfoStep4Request,
  BasicInfoStep4Response,
} from '@/types/api/registrationSteps';
import { OfferReport } from '@/types/models/offerReport';
import { JobSearch, SaveJobSearch } from '@/types/pages/SearchJob';
import { LocationQueryValue } from 'vue-router';

export const getBasicInformation = (signature?: string): Promise<BasicInformationResponse> => {
  return request(V1_BASIC_INFORMATION_PATH, {
    method: 'GET',
    params: { signature: signature },
  });
};

export const getJobSearchCondition = (signature?: string): Promise<ApiResponse> => {
  return request(V1_JOB_SEARCH_CONDITION, {
    method: 'GET',
    params: { signature: signature },
  });
};

export const getBasicInfoStep1 = (): Promise<BasicInfoStep1Response> => {
  return request(V1_BASIC_INFO_STEP1_PATH, {
    method: 'GET',
  });
};

export const saveBasicInfoStep1 = (data: BasicInfoStep1Request): Promise<ApiResponse> => {
  return request(V1_BASIC_INFO_STEP1_PATH, {
    method: 'POST',
    data,
  });
};

export const getBasicInfoStep2 = (): Promise<BasicInfoStep2Response> => {
  return request(V1_BASIC_INFO_STEP2_PATH, {
    method: 'GET',
  });
};

export const saveBasicInfoStep2 = (data: BasicInfoStep2Request): Promise<ApiResponse> => {
  return request(V1_BASIC_INFO_STEP2_PATH, {
    method: 'POST',
    data,
  });
};

export const getBasicInfoStep3 = (): Promise<BasicInfoStep3Response> => {
  return request(V1_BASIC_INFO_STEP3_PATH, {
    method: 'GET',
  });
};

export const saveBasicInfoStep3 = (data: BasicInfoStep3Request): Promise<ApiResponse> => {
  return request(V1_BASIC_INFO_STEP3_PATH, {
    method: 'POST',
    data,
  });
};

export const getBasicInfoStep4Completed = (): Promise<BasicInfoStep4CompletedResponse> => {
  return request(V1_BASIC_INFO_STEP4_COMPLETED_PATH, {
    method: 'GET',
    headers: {
      'Cache-Control': 'no-store',
      Pragma: 'no-cache',
    },
    params: {
      ts: Date.now(),
    },
  });
};

export const getBasicInfoStep4 = (): Promise<BasicInfoStep4Response> => {
  return request(V1_BASIC_INFO_STEP4_PATH, {
    method: 'GET',
  });
};

export const saveBasicInfoStep4 = (data: BasicInfoStep4Request): Promise<ApiResponse> => {
  return request(V1_BASIC_INFO_STEP4_PATH, {
    method: 'POST',
    data,
  });
};

export const createAccountEmail = (
  data: CreateAccountEmailRequest
): Promise<CreateAccountEmailResponse> => {
  return request(V1_SIGN_UP_PATH, {
    method: 'POST',
    data,
  });
};

export const getOccupations = (): Promise<ApiResponse> => {
  return request(V1_OCCUPATION_PATH, {
    method: 'GET',
  });
};

export const getCountries = (): Promise<ApiResponse> => {
  return request(V1_COUNTRY_PATH, {
    method: 'GET',
  });
};

export const getPrefectures = (): Promise<ApiResponse> => {
  return request(V1_PREFECTURE_PATH, {
    method: 'GET',
  });
};

export const getCitiesByPrefectures = (prefectureID?: number): Promise<ApiResponse> => {
  return request(`${V1_CITY_PATH}?prefecture_id=${prefectureID}`, {
    method: 'GET',
  });
};

export const getCities = (): Promise<ApiResponse> => {
  return request(V1_CITY_PATH, {
    method: 'GET',
  });
};

export const getProfile = (): Promise<ApiResponse> => {
  return request(V1_PROFILE_PATH, {
    method: 'GET',
  });
};

/** エージェント利用中求職者が企業スカウト（ダイレクト）利用を開始する（PATCH 成功後クライアントで求人一覧へ遷移する想定） */
export const patchWelcomeGuide = (): Promise<ApiResponse> => {
  return request(V1_WELCOME_GUIDE_PATH, {
    method: 'PATCH',
    data: {},
  });
};

export const applyJobInterview = (propositionId: number, message: string): Promise<ApiResponse> => {
  return request(`${V1_CANDIDATES_JOB_APPLY_PATH}/${propositionId}`, {
    method: 'PATCH',
    data: { message },
  });
};

export const createAgentService = (): Promise<ApiResponse> => {
  return request(V1_SERVICES_AGENT_PATH, {
    method: 'POST',
  });
};

export const updateProfile = (data: ProfileUpdateFormData): Promise<ApiResponse> => {
  return request(V1_PROFILE_PATH, {
    method: 'PATCH',
    headers: { 'Content-Type': 'multipart/form-data' },
    data,
  });
};

export const getIncomes = (): Promise<ApiResponse> => {
  return request(V1_INCOME_PATH, {
    method: 'GET',
  });
};

export const getEmploymentTypes = (condition: boolean): Promise<ApiResponse> => {
  return request(V1_EMPLOYMENT_TYPE_PATH, {
    method: 'GET',
    params: { search_condition: condition },
  });
};

export const getSchoolCategory = (): Promise<ApiResponse> => {
  return request(V1_SCHOOL_CATEGORY_PATH, {
    method: 'GET',
  });
};

export const getSkills = (): Promise<ApiResponse> => {
  return request(V1_SKILL_PATH, {
    method: 'GET',
  });
};

export const getIndustries = (): Promise<ApiResponse> => {
  return request(V1_INDUSTRIES_PATH, {
    method: 'GET',
  });
};

export const getMasterLanguageLevel = (): Promise<ApiResponse> => {
  return request(V1_LANGUAGE_LEVEL_PATH, {
    method: 'GET',
  });
};

export const getMasterLanguage = (): Promise<ApiResponse> => {
  return request(V1_LANGUAGE_PATH, {
    method: 'GET',
  });
};

export const getGraduationClassifications = (): Promise<ApiResponse> => {
  return request(V1_GRADUATION_CLASSIFICATION_PATH, {
    method: 'GET',
  });
};

export const registrationData = (data: RegistrationDataRequest): Promise<ApiResponse> => {
  return request(V1_REGISTRATION_PATH, {
    method: 'POST',
    data,
  });
};

export const createBasicInformation = (
  data: CreateBasicInformationRequest
): Promise<CreateBasicInformationResponse> => {
  return request(V1_BASIC_INFORMATION_PATH, {
    method: 'POST',
    data,
  });
};

export const saveResumeOptionalInformation = (
  data: SaveResumeOptionalInformationFormData
): Promise<ApiResponse> => {
  return request(V1_RESUME_OPTIONAL_INFORMATION_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
  });
};

export const login = (data: LoginRequest): Promise<LoginResponse> => {
  return request(V1_SESSION_PATH, {
    method: 'POST',
    data,
  });
};

export const saveAccessToken = (data: SaveAccessTokenRequest): Promise<ApiResponse> => {
  return request(V1_SESSION_PATH, {
    method: 'Patch',
    data,
  });
};

export const getJobFeature = (): Promise<ApiResponse> => {
  return request(V1_JOB_FEATURES_PATH, {
    method: 'GET',
  });
};

export const getExperiences = (): Promise<ApiResponse> => {
  return request(V1_EXPERIENCE_PATH, {
    method: 'GET',
  });
};

export const createJobSearchCondition = (
  data: CreateJobSearchConditionRequest
): Promise<ApiResponse> => {
  return request(V1_JOB_SEARCH_PATH, {
    method: 'POST',
    data,
  });
};

export const saveJobSearchCondition = (data: SaveJobSearch): Promise<ApiResponse> => {
  return request(V1_SAVE_CONDITION_JOB_SEARCH, {
    method: 'POST',
    data,
  });
};

export const addressWithZipCode = (zipCode?: string): Promise<AddressWithZipCodeResponse> => {
  return request(`${V1_ADDRESS_WITH_ZIP_CODE}?zip_code=${zipCode}`, {
    method: 'GET',
  });
};

export const createCandidateOfferReport = (data: OfferReport): Promise<ApiResponse> => {
  return request(V1_OFFER_REPORT_PATH, {
    method: 'POST',
    data,
  });
};

export const searchJob = (data: JobSearch): Promise<ApiResponse> => {
  return request(V1_SEARCH_JOB_PATH, {
    method: 'POST',
    data,
  });
};

export const verifyAccount = (
  signature: string | null | LocationQueryValue[]
): Promise<VerifyAccountResponse> => {
  return request(`${V1_CANDIDATES_VERIFY_ACCOUNT}/${signature}`, {
    method: 'GET',
  });
};

export const saveCandidateAccount = (
  password: string,
  token: string | null | LocationQueryValue[]
): Promise<SaveCandidateAccountResponse> => {
  return request(V1_CANDIDATES_SAVE_ACCOUNT, {
    method: 'POST',
    data: { token: token, password: password },
  });
};

export const saveCandidateSession = (signature: string | null): Promise<ApiResponse> => {
  return request(V1_COMPLETED_SIGN_UP_PATH, {
    method: 'POST',
    data: { token: signature },
  });
};

export const getCompanyListByName = (queryString: string | null): Promise<CompanyListResponse> => {
  return request(V1_BLOCKED_COMPANIES_PATH, {
    method: 'GET',
    params: { blocked_company_name: queryString },
  });
};

export const forgotPassword = (email: string): Promise<ApiResponse> => {
  return request(V1_CANDIDATE_RESET_PASSWORD, {
    method: 'POST',
    data: { email: email },
  });
};

export const resetPassword = (data: ConfirmPassword): Promise<ApiResponse> => {
  return request(V1_CANDIDATE_RESET_PASSWORD, {
    method: 'PATCH',
    data,
  });
};

export const verifyToken = (
  signature: string | null | LocationQueryValue[],
  type: string
): Promise<ApiResponse> => {
  return request(V1_CANDIDATES_VERIFY_TOKEN, {
    method: 'POST',
    data: { token: signature, type: type },
  });
};

export const resendMail = (email: string): Promise<ApiResponse> => {
  return request(V1_CANDIDATE_RESEND_MAIL, {
    method: 'GET',
    params: { email: email },
  });
};

export const getCandidateInterviewSlots = (): Promise<ApiResponse> => {
  return request(V1_CANDIDATE_INTERVIEW_SLOTS_PATH, {
    method: 'GET',
    params: {},
  });
};

export const createCandidateInterviewSlots = (
  payload: InterviewSlotCreatePayload
): Promise<ApiResponse> => {
  return request(V1_CANDIDATE_INTERVIEW_SLOTS_PATH, {
    method: 'POST',
    data: payload,
  });
};

export const updateCandidateInterviewSlot = (
  id: number,
  payload: InterviewSlotCreatePayload
): Promise<ApiResponse> => {
  return request(`${V1_CANDIDATE_INTERVIEW_SLOTS_PATH}/${id}`, {
    method: 'PATCH',
    data: payload,
  });
};

export const destroyCandidateInterviewSlots = (id: number): Promise<ApiResponse> => {
  return request(`${V1_CANDIDATE_INTERVIEW_SLOTS_PATH}/${id}`, {
    method: 'DELETE',
  });
};
